import { ESharedFunction } from "motw_core/Enums/ESharedFunction";
import {
    homeNode,
    workflowRepository,
} from "motw_core/Repositories/WorkflowRepository";
import { WithAnimatedProgressBarAsync } from "source/app/presentation/Header/ProgressBar/LevelProgressBar/WithAnimatedProgressBar";
import { IRootNodeFormState } from "source/app/presentation/MainPanel/Workflows/RootNodeName/Helpers/IRootNodeFormState";
import { NavigateTo } from "../../presentation/Routing/RouterNavigationTarget";
import ClientDataProvider from "../External/ClientDataProvider/ClientDataProvider";
import { EPanelId } from "../Shared/EPanelId";
import { Sounds } from "../Sound/Sounds";
import { ReadState } from "../UI/State/ReadState";
import UI from "../UI/UI";
import { DispatchEvent } from "../Events/Events";
import { EEventType } from "../Events/EEventType";
import { GetWorkflowsState } from "source/app/presentation/MainPanel/Workflows/Workflows";
import { RouteWorkflowsAsync } from "source/app/presentation/Shared/ActivePanel/Routing/RouteWorkflows";
import { LogWorkflowNavigation } from "./WorkflowNavigationDiagnostics";

export async function SetRootNodeAsync(id: number) {
    LogWorkflowNavigation("SetRootNode:start", { targetId: id });

    const isEditingWorkflowNode = ReadState<IRootNodeFormState>(
        ESharedFunction.RootNodeName_state
    )?.showEditControls;

    if (isEditingWorkflowNode) {
        LogWorkflowNavigation("SetRootNode:skip-editing", { targetId: id });
        return;
    }

    const currentRootNode = GetWorkflowsState().state.rootNode;
    const shouldStartLoadingSaga = currentRootNode.id !== id;

    if (shouldStartLoadingSaga) {
        LogWorkflowNavigation("SetRootNode:dispatch-start-loading", {
            currentId: currentRootNode.id,
            targetId: id,
        });
        DispatchEvent(EEventType.Workflows_RootNodeChange_StartLoading);
    } else {
        LogWorkflowNavigation("SetRootNode:skip-same-root", { targetId: id });
    }

    let foundNode = false;

    try {
        LogWorkflowNavigation("SetRootNode:existence-check-start", {
            targetId: id,
        });

        foundNode =
            id === homeNode.id ||
            (await WithAnimatedProgressBarAsync(
                () =>
                    workflowRepository.DoesExactNodeExistById(
                        ClientDataProvider,
                        id
                    ),
                `SetRootNode_DoesExactNodeExistById_${id}`
            ));

        LogWorkflowNavigation("SetRootNode:existence-check-finish", {
            targetId: id,
            foundNode,
        });
    } catch (e) {
        LogWorkflowNavigation("SetRootNode:existence-check-error", {
            targetId: id,
            error: GetErrorMessage(e),
        });

        if (shouldStartLoadingSaga) {
            DispatchEvent(EEventType.Workflows_RootNodeChange_FinishLoading);
        }

        throw e;
    }

    try {
        Sounds.snapsMultiple.Play({ allowConcurrent: true });

        const targetUrl = foundNode
            ? `/${EPanelId[EPanelId.Workflows]}/${id}`
            : `/${EPanelId[EPanelId.Workflows]}`;

        if (!foundNode && shouldStartLoadingSaga) {
            LogWorkflowNavigation("SetRootNode:dispatch-finish-no-node", {
                targetId: id,
            });
            DispatchEvent(EEventType.Workflows_RootNodeChange_FinishLoading);
        }

        Sounds.clangSpacey.Play({ allowConcurrent: true });

        console.log(`[Routing] SetRootNode navigating to '${targetUrl}'`);
        LogWorkflowNavigation("SetRootNode:navigate", {
            targetId: id,
            targetUrl,
        });
        if (window.location.pathname === targetUrl) {
            LogWorkflowNavigation("SetRootNode:route-current-url", {
                targetId: id,
                targetUrl,
            });
            await RouteWorkflowsAsync({
                panelName: EPanelId[EPanelId.Workflows],
                elementId: `${id}`,
            });
        } else {
            NavigateTo(targetUrl);
        }

        UI.ScrollToDiv(`#calcTarget_workflows`);
    } catch (e) {
        LogWorkflowNavigation("SetRootNode:navigation-error", {
            targetId: id,
            error: GetErrorMessage(e),
        });

        if (shouldStartLoadingSaga) {
            DispatchEvent(EEventType.Workflows_RootNodeChange_FinishLoading);
        }

        throw e;
    }
}

function GetErrorMessage(e: unknown) {
    return e instanceof Error ? e.message : String(e);
}
